import {
  FavoriteAction,
  FinalizeAction,
  QuickReportData,
  RawReportData, AdditionalReportMetadata
} from '@shared/interfaces';

export enum ActionTypes {
  GET_REPORTS = '[Report] GetReports',
  GET_PUBLIC_REPORTS = '[Report] GetPublicReports',
  GET_REPORT_BY_ID = '[Report] GetReportById',
  GET_NPI_DATA_BY_REPORT = '[Report] GetNpiDataByReport',
  GET_ALREADY_CREATED_REPORTS = '[Report] GetAlreadyCreatedReports',
  OBSERVE_REPORT_BY_ID = '[Report] ObserveReportById',
  GET_RESULT_BY_ID = '[Report] GetResultById',
  GET_SIMULATION_RESULT_BY_ID = '[Quick] GetSimulationResultById',
  OBSERVE_SIMULATION_RESULT_BY_ID = '[Quick] ObserveSimulationResultById',
  GET_SIMULATION_DATAPOINT_BY_ID = '[Quick] GetSimulationDataPointById',
  OBSERVE_SIMULATION_DATAPOINT_BY_ID = '[Quick] ObserveSimulationDataPointById',
  CREATE_QUICK_REPORT = '[Quick] CreateQuickReport',
  UPDATE_QUICK_REPORT = '[Quick] UpdateQuickReport',
  CREATE_FULL_REPORT = '[Report] CreateFullReport',
  CHECK_INVENTORY_EXISTENCE = '[Report] CheckInventoryExistence',
  REFRESH_REPORT = '[Report] RefreshReport',
  GET_CAR_RECALLS = '[Report] GetCarRecalls',
  GET_VEHICLE_DATA = '[Report] GetVehicleDataByReportResult',
  GET_VEHICLE_DATA_BY_ID = '[Report] GetVehicleDataById',
  GET_STATS = '[Report] GetStatsByFilter',
  GET_DEALER = '[Report] GetDealerByFilter',
  GET_FAVORITE_REPORTS_ID = '[Report] GetFavoriteReportsId',
  SET_FAVORITE_REPORT = '[Report] SetFavoriteReport',
  GET_FUEL_ECONOMY_DATA = '[Report] GetFuelEconomyData',
  GET_GAS_PRICE = '[Report] GetGasPrice',
  GET_ELECTRICITY_PRICE = '[Report] GetElectricityPrice',
  GET_POSSIBLE_REPORTS_INVENTORY = '[Report] GetPossibleReportsInventory',
  GET_LISTING_FILTERS = '[Report] GetListingFilters',
}

export class GetReports {
  static readonly type = ActionTypes.GET_REPORTS;
}

export class GetPublicReports {
  static readonly type = ActionTypes.GET_PUBLIC_REPORTS;
}

export class GetReportById {
  static readonly type = ActionTypes.GET_REPORT_BY_ID;

  constructor(public payload: { reportId: string; }) {
  }
}

export class GetAlreadyCreatedReports {
  static readonly type = ActionTypes.GET_ALREADY_CREATED_REPORTS;
}

export class GetResultById {
  static readonly type = ActionTypes.GET_RESULT_BY_ID;

  constructor(public payload: { reportId: string, resultId: string; }) {
  }
}

export class GetSimulationResultsById {
  static readonly type = ActionTypes.GET_SIMULATION_RESULT_BY_ID;

  constructor(public payload: { reportId: string, simulationId: string; }) {
  }
}

export class GetSimulationDataPointsById {
  static readonly type = ActionTypes.GET_SIMULATION_DATAPOINT_BY_ID;

  constructor(public payload: { reportId: string, simulationId: string; }) {
  }
}

export class ObserveSimulationResultsById {
  static readonly type = ActionTypes.OBSERVE_SIMULATION_RESULT_BY_ID;

  constructor(public payload: { reportId: string, simulationId?: string; }) {
  }
}

export class ObserveSimulationDataPointsById {
  static readonly type = ActionTypes.OBSERVE_SIMULATION_DATAPOINT_BY_ID;

  constructor(public payload: { reportId: string, simulationId?: string; }) {
  }
}

export class ObserveReportById {
  static readonly type = ActionTypes.OBSERVE_REPORT_BY_ID;

  constructor(public payload: { reportId: string; action: FinalizeAction }) {
  }
}

export class CreateFullReport {
  static readonly type = ActionTypes.CREATE_FULL_REPORT;

  constructor(public payload: { reportData: RawReportData }) {
  }
}

export class CreateQuickReport {
  static readonly type = ActionTypes.CREATE_QUICK_REPORT;

  constructor(public payload: { simulatedInfoData: QuickReportData, additionalReportMetadata: AdditionalReportMetadata }) {
  }
}

export class CheckInventoryExistence {
  static readonly type = ActionTypes.CHECK_INVENTORY_EXISTENCE;

  constructor(public payload: { quickInfoData: QuickReportData }) {
  }
}

export class UpdateQuickReport {
  static readonly type = ActionTypes.UPDATE_QUICK_REPORT;

  constructor(public payload: { additionalReportMetadata: AdditionalReportMetadata }) {
  }
}

export class RefreshReport {
  static readonly type = ActionTypes.REFRESH_REPORT;

  constructor(public payload: { reportId: string; reportData: RawReportData; action: FinalizeAction }) {
  }
}

export class GetCarRecalls {
  static readonly type = ActionTypes.GET_CAR_RECALLS;

  constructor(public payload: { reportId: string; resultId: string; }) {
  }
}

export class GetVehicleDataByReportResult {
  static readonly type = ActionTypes.GET_VEHICLE_DATA;

  constructor(public payload: { reportId: string; resultId: string; sectionId: string }) {
  }
}

export class GetVehicleDataById {
  static readonly type = ActionTypes.GET_VEHICLE_DATA_BY_ID;

  constructor(public payload: { reportId: string; resultId: string; vehicleId: string }) {
  }
}

export class GetStatsByFilter {
  static readonly type = ActionTypes.GET_STATS;

  constructor(public payload: { reportId: string; resultId: string; sectionId: string }) {
  }
}

export class GetDealerByFilter {
  static readonly type = ActionTypes.GET_DEALER;

  constructor(public payload: { reportId: string; resultId: string; sectionId: string }) {
  }
}

export class GetNpiDataByReport {
  static readonly type = ActionTypes.GET_NPI_DATA_BY_REPORT;

  constructor(public payload: { reportId: string; resultId: string; npiDoc: 'email' }) {
  }
}

export class GetFavoriteReportsId {
  static readonly type = ActionTypes.GET_FAVORITE_REPORTS_ID;
}

export class SetFavoriteReport {
  static readonly type = ActionTypes.SET_FAVORITE_REPORT;

  constructor(public payload: { reportId: string; action: FavoriteAction; }) {
  }
}

export class GetGasPrice {
  static readonly type = ActionTypes.GET_GAS_PRICE;

  constructor(public payload: { zipcode?: string; state?: string; }) {
  }
}

export class GetElectricityPrice {
  static readonly type = ActionTypes.GET_ELECTRICITY_PRICE;

  constructor(public payload: { state?: string; }) {
  }
}

export class GetPossibleReportsInventory {
  static readonly type = ActionTypes.GET_POSSIBLE_REPORTS_INVENTORY;

  constructor(public payload: { reportId?: string; resultId?: string; possibleReportId?: string; }) {
  }
}

export class GetListingFilters {
  static readonly type = ActionTypes.GET_LISTING_FILTERS;

  constructor(public payload: { reportId: string; resultId: string; sectionId: string; }) {
  }
}

export class GetFuelEconomyData {
  static readonly type = ActionTypes.GET_FUEL_ECONOMY_DATA;

  constructor(public payload: { reportId?: string; resultId?: string; }) {
  }
}
